/**
 * SynthMusicDirector — the game's music: seeded synth tracks (compose5.ts —
 * v4's themes performed like v2, the place picks its genre, v2's pulse lead)
 * played live through the MUSIC bus (settings volume/mute apply).
 *
 * Main game: planet surface → the place's scene (large/small city,
 * exploration) with day/night, using the chamber director's footprint
 * hysteresis so borders don't flip tracks; ship flight → the Space-drive style
 * seeded by the last world; COMBAT → the world's battle track (scene 'fight'),
 * cut in fast from its opening hit. Race: an explicit request (course seed + world).
 *
 * A new request crossfades on the beat grid; hidden tab / muted / suspended
 * context stops scheduling; races resume at the shared round clock.
 */
import type { AudioEngine } from '../engine';
import { PlaceContextSelector, type PlaceContext } from '../place/context';
import type { MusicBiome } from '../place/score';
import { composeSynth5 } from './compose5';
import type { SynthRequest } from './compose';
import { SynthPlayer } from './player';
import { MusicReactivity } from './reactivity';

const keyOf = (r: SynthRequest) => JSON.stringify([r.seed, r.biome, r.scene, r.night]);
const FADE_IN = 2.5, FADE_OUT = 3.5;
/** Combat cuts in: the battle track starts from its opening hit almost at once. */
const FIGHT_IN = .25, FIGHT_OUT = 1.2;
/** The game's composer settings (the lab's v5 defaults). */
export const composeGameMusic = (r: SynthRequest, bpm?:number) => {
 const score=composeSynth5(r,{genre:bpm?'synthwave':'place',lead:'pulse'});
 return bpm?{...score,bpm,duration:score.beats*60/bpm}:score;
};
/** Shared game clock, sampled again after async calibration (never wall-clock timers). */
export interface MusicTransport {key:string;bpm:number;time:()=>number}

export class SynthMusicDirector {
  private readonly reactivity = new MusicReactivity();
  private selector = new PlaceContextSelector();
  private active: { key: string; player: SynthPlayer } | null = null;
  private pending: { key: string; cancelled: boolean } | null = null;
  private retiring: SynthPlayer[] = [];
  private explicit: SynthRequest | null = null;
  private world: { seed: string; biome: MusicBiome } | null = null;
  private desired: SynthRequest | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastError: string | null = null;
  private transport:MusicTransport|null=null;

  constructor(private engine: AudioEngine) {
    document.addEventListener('visibilitychange', () => this.evaluate());
    window.addEventListener('pagehide', () => this.halt(0.05));
  }

  /** Main game, every frame: the surface place (or null), whether the ship is flying, and
   *  whether the player is in a fight (the caller owns the enter/hold hysteresis). */
  update(place: PlaceContext | null, flying: boolean, fighting = false): void {
    const now = this.engine.ctx.currentTime;
    if (place) this.world = { seed: place.seed, biome: place.biome };
    const surface = this.selector.update(place, now);
    const calm: SynthRequest | null = surface ?? (flying ? { seed: this.world?.seed ?? 'deep-space', biome: this.world?.biome ?? 'barren', scene: 'flight', night: false } : null);
    // Each world has its own battle theme (seeded by the world, darker at night).
    this.desired = fighting && (surface || flying)
      ? { seed: this.world?.seed ?? 'deep-space', biome: this.world?.biome ?? 'barren', scene: 'fight', night: surface?.night ?? false }
      : calm;
    this.evaluate();
  }

  /** Explicit request (race game); null = silence. Keeps itself alive without per-frame calls. */
  play(request: SynthRequest | null,transport:MusicTransport|null=null): void {
    this.transport=transport;
    this.explicit = request;
    this.desired = request;
    if (request && !this.timer) this.timer = setInterval(() => this.evaluate(), 500);
    if (!request && this.timer) { clearInterval(this.timer); this.timer = null; }
    this.evaluate();
  }

  get status() {
    const s = this.active?.player.score;
    return {
      playing: this.active?.key ?? null, seed: s?.request.seed ?? null, scene: s?.request.scene ?? null, night: s?.request.night ?? null,
      scheduled: this.active?.player.playing ? 1 : 0, title: s?.title ?? null, style: s ? `${s.style} · ${s.keyName} ${s.mode} · ${s.bpm} BPM` : null,
      pending: this.pending?.key ?? null, retiring: this.retiring.length, error: this.lastError,
      beat:this.active?.player.beat??0,bpm:s?.bpm??0,transportBeat:this.transport?this.transport.time()*this.transport.bpm/60:null,
    };
  }

  sampleMusic(dt: number) {
    const player = this.active?.player;
    const audible = this.audible() && !!player?.playing;
    return this.reactivity.sample(audible ? player?.engine?.analyser ?? null : null,
      this.engine.ctx.sampleRate, this.transport?this.transport.time()*this.transport.bpm/60:player?.beat ?? 0,
      audible ? this.engine.getMasterVolume() * this.engine.getBusVolume('music') : 0, dt);
  }

  private audible(): boolean {
    return !document.hidden && this.engine.ctx.state === 'running' && !this.engine.isMuted() && this.engine.getBusVolume('music') > 0 && this.engine.getMasterVolume() > 0;
  }

  private evaluate(): void {
    const want = this.explicit ?? this.desired;
    if (!want || !this.audible()) {
      if (this.active || this.pending) this.halt(want ? .3 : FADE_OUT);
      return;
    }
    const key = keyOf(want)+(this.transport?`|${this.transport.key}|${this.transport.bpm}`:'');
    if (this.active?.key === key || this.pending?.key === key) return;
    if (this.pending) this.pending.cancelled = true;
    const job = { key, cancelled: false };
    this.pending = job;
    void this.start(want, job);
  }

  private async start(request: SynthRequest, job: { key: string; cancelled: boolean }): Promise<void> {
    try {
      // Compose off the current frame; calibration renders offline (async).
      await new Promise(r => setTimeout(r, 0));
      if (job.cancelled) return;
      // Same live engine as the Synth Music Lab (the user's reference for how it
      // should sound), with a long lookahead so game frame hitches can't make notes late.
      const player = new SynthPlayer(this.engine.ctx, composeGameMusic(request,this.transport?.bpm), this.engine.busInput('music'), 1.2);
      await player.prepare();
      if (job.cancelled || this.pending !== job) { player.dispose(); return; }
      // Into a fight: cut. Out of one (or anything else): the usual slow crossfade.
      const fight = request.scene === 'fight', fromFight = this.active?.player.score.request.scene === 'fight';
      this.retire(fight && !fromFight ? FIGHT_OUT : FADE_OUT);
      const transport=this.transport;
      const fromBeat=transport?()=> transport.time()*transport.bpm/60:0;
      await player.start(fromBeat, fight ? FIGHT_IN : FADE_IN);
      this.active = { key: job.key, player };
      this.pending = null;
      this.lastError = null;
    } catch (error) {
      if (this.pending === job) this.pending = null;
      this.lastError = String(error);
      console.warn('[audio] synth music unavailable', error);
    }
  }

  private retire(fade: number): void {
    if (!this.active) return;
    const p = this.active.player; this.active = null;
    p.stop(fade); this.retiring.push(p);
    setTimeout(() => { p.dispose(); this.retiring = this.retiring.filter(x => x !== p); }, (fade + 1) * 1000);
  }

  private halt(fade: number): void {
    if (this.pending) { this.pending.cancelled = true; this.pending = null; }
    this.retire(fade);
  }
}
